import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useResubmitAdminProfile } from '@/hooks/use-admin';
import { getTokenStore } from '@shared/api/token-store';
import { useAuthStore } from '@shared/stores/auth-store';
import type { AdminRole, PublicUser } from '@shared/types/api';

const OPERABLE_ADMIN_ROLES: { value: AdminRole; label: string }[] = [
  { value: 'OH_SERVICE_MANAGER', label: 'OH / Service Manager' },
  { value: 'ASSIGNER', label: 'Assigner' },
  { value: 'CASE_EXECUTIVE', label: 'Case Executive' },
  { value: 'ASSOCIATE_CONTROLLING_EXECUTIVE', label: 'Associate Controlling Executive' },
  { value: 'CUSTOMER_SUPPORT_EXECUTIVE', label: 'Customer Support Executive' },
  { value: 'FINANCE_EXECUTIVE', label: 'Finance Executive' },
  { value: 'AUDITOR_COMPLIANCE_REVIEWER', label: 'Auditor / Compliance Reviewer' },
];

export function AdminPendingApprovalView({ user }: { user: PublicUser }) {
  const signOut = useAuthStore((s) => s.signOut);

  function handleSignOut() {
    getTokenStore().clear();
    signOut();
    window.location.href = '/login';
  }

  const roleLabel =
    OPERABLE_ADMIN_ROLES.find((r) => r.value === user.adminRole)?.label ||
    user.adminRole?.replace(/_/g, ' ') ||
    'Administrative Staff';

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <header className="border-b border-gray-200 bg-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <img src="/ecregi_logo.png" alt="EcRegi" className="h-8 w-8 object-contain" />
          <span className="text-base font-bold text-gray-900">EcRegi Admin Console</span>
        </div>
        <button
          onClick={handleSignOut}
          className="text-xs font-semibold text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg px-3 py-1.5 transition-colors"
        >
          Sign Out
        </button>
      </header>

      <main className="flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-8 shadow-card text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 border border-amber-200">
            <svg className="h-8 w-8 text-amber-600 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>

          <span className="mt-4 inline-block rounded-full bg-amber-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-amber-800">
            Status: Under Review
          </span>

          <h2 className="mt-3 text-xl font-bold text-gray-900">Admin Application Pending</h2>
          <p className="mt-2 text-xs text-gray-500 leading-relaxed max-w-sm mx-auto">
            Your registration details have been submitted. A Super Admin must review and accept your profile before administrative tools are unlocked.
          </p>

          <div className="mt-6 rounded-xl border border-gray-100 bg-gray-50/80 p-4 text-left space-y-2.5 text-xs">
            <div className="flex justify-between items-center py-1 border-b border-gray-200/50">
              <span className="text-gray-500">Applicant:</span>
              <span className="font-semibold text-gray-900">
                {user.firstName} {user.lastName}
              </span>
            </div>
            <div className="flex justify-between items-center py-1 border-b border-gray-200/50">
              <span className="text-gray-500">Registered Email:</span>
              <span className="font-semibold text-gray-900">{user.email}</span>
            </div>
            <div className="flex justify-between items-center py-1 border-b border-gray-200/50">
              <span className="text-gray-500">Mobile Number:</span>
              <span className="font-semibold text-gray-900">{user.mobile || '—'}</span>
            </div>
            <div className="flex justify-between items-center py-1">
              <span className="text-gray-500">Applied Role:</span>
              <span className="font-bold text-brand-700 bg-brand-50 border border-brand-200 px-2 py-0.5 rounded text-[11px]">
                {roleLabel}
              </span>
            </div>
          </div>

          <p className="mt-6 text-[11px] text-gray-400">
            Please check back later or contact your system administrator.
          </p>
        </div>
      </main>
    </div>
  );
}

export function AdminRejectedView({ user }: { user: PublicUser }) {
  const signOut = useAuthStore((s) => s.signOut);
  const resubmitMutation = useResubmitAdminProfile();

  const [firstName, setFirstName] = useState(user.firstName || '');
  const [lastName, setLastName] = useState(user.lastName || '');
  const [mobile, setMobile] = useState(user.mobile || '');
  const [adminRole, setAdminRole] = useState<AdminRole>(
    (user.adminRole as AdminRole) || 'OH_SERVICE_MANAGER',
  );

  function handleSignOut() {
    getTokenStore().clear();
    signOut();
    window.location.href = '/login';
  }

  function handleResubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) return;
    if (!mobile.trim() || mobile.trim().length < 10) return;

    resubmitMutation.mutate({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      mobile: mobile.trim(),
      adminRole,
    });
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <header className="border-b border-gray-200 bg-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <img src="/ecregi_logo.png" alt="EcRegi" className="h-8 w-8 object-contain" />
          <span className="text-base font-bold text-gray-900">EcRegi Admin Console</span>
        </div>
        <button
          onClick={handleSignOut}
          className="text-xs font-semibold text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg px-3 py-1.5 transition-colors"
        >
          Sign Out
        </button>
      </header>

      <main className="flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-lg rounded-2xl border border-red-200 bg-white p-7 shadow-card">
          <div className="flex items-center gap-3 border-b border-red-100 pb-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-red-100 text-red-600 shrink-0">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Application Declined by Super Admin</h2>
              <p className="text-xs text-gray-500">
                Please review the reason below, update your details, and resubmit.
              </p>
            </div>
          </div>

          {/* Super Admin Reason Box */}
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-red-700">
              Reason Provided by Super Admin:
            </div>
            <div className="mt-1 text-xs text-red-900 font-medium whitespace-pre-wrap leading-relaxed">
              &ldquo;{user.rejectionReason || 'No specific reason provided.'}&rdquo;
            </div>
          </div>

          {/* Profile Correction Form */}
          <form onSubmit={handleResubmit} className="mt-5 space-y-3.5">
            {/* Locked Email */}
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                Email Address <span className="text-gray-400 font-normal">(Cannot be modified)</span>
              </label>
              <Input
                type="email"
                value={user.email}
                disabled
                className="bg-gray-100 cursor-not-allowed opacity-80"
              />
            </div>

            {/* Editable Names */}
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Editable Mobile */}
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <Input
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                required
              />
            </div>

            {/* Editable Role */}
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                Requested Role <span className="text-red-500">*</span>
              </label>
              <select
                value={adminRole}
                onChange={(e) => setAdminRole(e.target.value as AdminRole)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs text-gray-900 focus:border-brand-600 focus:outline-hidden focus:ring-1 focus:ring-brand-600"
                required
              >
                {OPERABLE_ADMIN_ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>

            <Button
              type="submit"
              block
              disabled={resubmitMutation.isPending}
              className="mt-2"
            >
              {resubmitMutation.isPending ? 'Updating & Resubmitting…' : 'Update & Resubmit for Verification'}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}

const ROLE_LABELS: Record<AdminRole, string> = {
  SUPER_ADMIN: 'Super Admin',
  OH_SERVICE_MANAGER: 'OH / Service Manager',
  ASSIGNER: 'Assigner',
  CASE_EXECUTIVE: 'Case Executive',
  ASSOCIATE_CONTROLLING_EXECUTIVE: 'Associate Controlling Executive',
  CUSTOMER_SUPPORT_EXECUTIVE: 'Customer Support Executive',
  FINANCE_EXECUTIVE: 'Finance Executive',
  AUDITOR_COMPLIANCE_REVIEWER: 'Auditor / Compliance Reviewer',
  OPS_ADMIN: 'Ops Admin',
  READ_ONLY_ADMIN: 'Read Only Admin',
  ASSIGNER_ADMIN: 'Assigner Admin',
};

export function AdminNoPermissionView({ adminRole }: { adminRole: AdminRole | null }) {
  const roleLabel = adminRole ? (ROLE_LABELS[adminRole] ?? adminRole.replace(/_/g, ' ')) : 'Admin';

  return (
    <div className="flex min-h-full flex-col items-center justify-center px-6 py-24 bg-gray-50">
      {/* Illustration */}
      <div className="mb-8 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-brand-100 to-teal-100 shadow-inner">
        <svg
          className="h-12 w-12 text-brand-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
          />
        </svg>
      </div>

      {/* Heading */}
      <h1 className="text-2xl font-bold text-gray-900 text-center">
        Welcome, {roleLabel}!
      </h1>
      <p className="mt-2 text-sm font-semibold text-brand-600 uppercase tracking-wider">
        Account Active
      </p>

      {/* Divider */}
      <div className="mt-6 w-16 border-t-2 border-brand-200" />

      {/* Message */}
      <div className="mt-6 max-w-md text-center space-y-3">
        <p className="text-gray-700 text-base leading-relaxed">
          Your account has been verified and is active. However, you don't have any
          section permissions assigned yet.
        </p>
        <p className="text-gray-500 text-sm leading-relaxed">
          Please contact the <span className="font-semibold text-brand-700">Super Admin</span> to
          get the appropriate access permissions for your role.
        </p>
      </div>

    </div>
  );
}
